import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Trash2, Cookie } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob, api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openCookieSettings } from "@/components/Chrome";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function PrivacySettings() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const del = async () => {
    try {
      await api.post("/privacy/delete", { confirm });
      setUser(false);
      toast.success("Your account and personal data have been erased.");
      nav("/");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="privacy-portal" className="max-w-3xl reveal">
      <div className="eyebrow">Settings · Privacy portal</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2">Your data, your rights.</h1>
      <div className="space-y-4 mt-10">
        <div className="surface p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="flex-1"><div className="font-display text-xl font-bold">Download my data</div><p className="text-sm text-zinc-400">Export your account, tracks, comments, likes, follows, payments and Co-Pilot history as JSON (GDPR art. 20).</p></div>
          <button data-testid="privacy-export-button" onClick={() => downloadBlob("/privacy/export", "soundra-my-data.json").then(() => toast.success("Export downloaded"))} className="h-11 px-5 rounded-full bg-white text-black font-semibold inline-flex items-center gap-2"><Download size={16} /> Export JSON</button>
        </div>
        <div className="surface p-6 flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="flex-1"><div className="font-display text-xl font-bold">Cookie preferences</div><p className="text-sm text-zinc-400">Change your analytics and persistent storage consent.</p></div>
          <button data-testid="privacy-cookie-button" onClick={openCookieSettings} className="h-11 px-5 rounded-full border border-white/15 inline-flex items-center gap-2"><Cookie size={16} /> Manage cookies</button>
        </div>
        <div className="surface p-6 flex flex-col sm:flex-row gap-4 sm:items-center border-red-500/30">
          <div className="flex-1"><div className="font-display text-xl font-bold text-red-400">Delete my account</div><p className="text-sm text-zinc-400">Right to be Forgotten: removes your profile, tracks, comments and social graph. Legally required invoices are retained in anonymised form.</p></div>
          <button data-testid="privacy-delete-button" onClick={() => setOpen(true)} className="h-11 px-5 rounded-full bg-red-500/90 text-white font-semibold inline-flex items-center gap-2"><Trash2 size={16} /> Delete account</button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#12121A] border-white/10" data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">This cannot be undone</DialogTitle><DialogDescription>Type DELETE to permanently erase your Soundra account.</DialogDescription></DialogHeader>
          <Input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" className="bg-white/5 border-white/10" />
          <button data-testid="delete-confirm-button" disabled={confirm !== "DELETE"} onClick={del} className="h-11 rounded-full bg-red-500 text-white font-semibold disabled:opacity-40">Permanently delete</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
